import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  ConfirmationDialog,
  Input,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import type { VirtualizedComboboxOption } from '@roger/ui-components';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { Field, DropdownField } from '../FormFields';
import { ClinicSelect } from '../clinic/ClinicSelect';
import type {
  AgencyClinicResponse,
  BillingFrequency,
  ChargeType,
  PayorSearchResult,
  PayorType,
} from '../../services/clinicsApi';
import { PAYOR_TYPE_LABELS, searchPayors } from '../../services/clinicsApi';
import { isMedicarePayorType } from './insuranceUtils';
import InsuranceRatesPanel from './InsuranceRatesPanel';
import {
  BILLING_FREQUENCY_OPTIONS,
  INSURANCE_CHARGE_TYPE_OPTIONS,
} from '../../constants/insuranceBilling';

export type InsuranceModalTab = 'details' | 'rates';

export type InsuranceForm = {
  agency_clinic_id: string;
  name: string;
  edi_payer_id: string;
  edi_clearinghouse: string;
  visit_authorization_required: boolean;
  noa_required: boolean;
  payor_type: PayorType | null;
  billing_frequency: BillingFrequency;
  pdgm_enabled: boolean;
  charge_type: ChargeType;
  do_not_multiply_charges_by_units: boolean;
  health_plan_id: string;
  provider_code: string;
  provider_number: string;
  address_one: string;
  address_two: string;
  city: string;
  state: string;
  zip_code: string;
};

type InsuranceModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  form: InsuranceForm;
  onChange: (updates: Partial<InsuranceForm>) => void;
  onSave: () => void;
  onCancel: () => void;
  clinics: AgencyClinicResponse[];
  saving: boolean;
  // When true, this insurance is synced from WellSky (clinic_key set): the
  // modal is view-only because clinic-info sync overwrites these fields.
  wellSkySynced?: boolean;
  // Gates the new billing-config section (billing frequency, charge type,
  // health plan ID, PDGM, do-not-multiply) behind the edit-insurance-billing
  // feature flag. When false the section is hidden and those fields keep their
  // form defaults. Defaults false so the feature stays dark unless explicitly enabled.
  showBillingConfig?: boolean;
  // The persisted insurance being edited; null in the create flow. The
  // Billing rates tab needs it (rate rows attach to an insurance id), so the
  // tab is disabled until the insurance is saved. Rates stay editable even
  // for WellSky-synced insurances (unlike the details form), so the tab is
  // intentionally not gated by isViewOnly.
  insuranceId?: string | null;
  // Tab the modal opens on; a table-row rates action deep-links to 'rates'.
  initialTab?: InsuranceModalTab;
};

const PAYOR_TYPE_OPTIONS: VirtualizedComboboxOption[] = (
  Object.entries(PAYOR_TYPE_LABELS) as [PayorType, string][]
).map(([value, label]) => ({ value, label }));

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-4 mb-2'>
    {children}
  </p>
);

const SwitchRow: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}> = ({ id, label, checked, onCheckedChange, disabled }) => (
  <div className='flex items-center gap-3'>
    <Switch
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
    />
    <label
      htmlFor={id}
      className={`text-sm font-medium ${
        disabled ? 'text-muted-foreground' : 'cursor-pointer'
      }`}
    >
      {label}
    </label>
  </div>
);

const InsuranceModal: React.FC<InsuranceModalProps> = ({
  open,
  mode,
  form,
  onChange,
  onSave,
  onCancel,
  clinics,
  saving,
  wellSkySynced = false,
  showBillingConfig = false,
  insuranceId = null,
  initialTab = 'details',
}) => {
  const [payorSearchQuery, setPayorSearchQuery] = useState('');
  const [payorSearchResults, setPayorSearchResults] = useState<
    PayorSearchResult[]
  >([]);
  const [isSearchingPayors, setIsSearchingPayors] = useState(false);
  const [showPayorResults, setShowPayorResults] = useState(false);

  const handlePayorSearch = async () => {
    if (!payorSearchQuery.trim()) return;
    setIsSearchingPayors(true);
    setShowPayorResults(true);
    try {
      const results = await searchPayors(payorSearchQuery.trim());
      setPayorSearchResults(results);
    } catch (error) {
      console.error('Failed to search payors:', error);
      setPayorSearchResults([]);
    } finally {
      setIsSearchingPayors(false);
    }
  };

  const handleSelectPayor = (payor: PayorSearchResult) => {
    onChange({
      name: payor.payor_name,
      edi_payer_id: payor.electronic_payor_id || '',
    });
    setShowPayorResults(false);
    setPayorSearchQuery('');
  };

  // Reset payer-lookup state when the modal closes so a new create/edit
  // session never shows the previous search query or results.
  useEffect(() => {
    if (!open) {
      setPayorSearchQuery('');
      setPayorSearchResults([]);
      setShowPayorResults(false);
    }
  }, [open]);

  // Billing rates live on their own tab (leadership: primary details stay on
  // the main tab, rates get a dedicated one). The tab needs a persisted
  // insurance to attach rows to.
  const canEditRates = mode === 'edit' && !!insuranceId;
  const showRatesTab = showBillingConfig;
  const [activeTab, setActiveTab] = useState<InsuranceModalTab>('details');
  // The rates panel mounts lazily on the first visit to its tab (so a
  // Details-only edit never fetches the rates grid or surfaces its load
  // toasts), then stays mounted so in-progress rate edits survive tab
  // switches.
  const [ratesTabVisited, setRatesTabVisited] = useState(false);
  const [ratesCount, setRatesCount] = useState<number | null>(null);
  const [ratesDirty, setRatesDirty] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Each open starts fresh on the requested tab (deep-links land on 'rates').
  useEffect(() => {
    if (open) {
      const startOnRates =
        initialTab === 'rates' && showRatesTab && canEditRates;
      setActiveTab(startOnRates ? 'rates' : 'details');
      setRatesTabVisited(startOnRates);
      setRatesCount(null);
      setRatesDirty(false);
    }
  }, [open, initialTab, showRatesTab, canEditRates]);

  // Closing with unsaved rate edits needs a confirm — the rates tab keeps its
  // own state and would otherwise be discarded silently.
  const requestClose = () => {
    if (ratesDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onCancel();
    }
  };

  // A successful details save also closes the modal (the parent's success
  // path), so it is a close for the rates tab's purposes: saving with dirty
  // rate edits needs the same explicit confirm, not a silent discard.
  const [confirmSaveDiscardOpen, setConfirmSaveDiscardOpen] = useState(false);
  const requestSave = () => {
    if (ratesDirty) {
      setConfirmSaveDiscardOpen(true);
    } else {
      onSave();
    }
  };

  const isViewOnly = wellSkySynced;

  const isSaveDisabled =
    isViewOnly ||
    saving ||
    !form.name.trim() ||
    (mode === 'create' && (!form.agency_clinic_id || !form.payor_type));

  return (
    <>
      <Dialog open={open} onOpenChange={isOpen => !isOpen && requestClose()}>
        <DialogContent
          // The details form reads best narrow; the rates grid needs room. The
          // width follows the active tab so the modal "opens up" for the grid.
          className={`max-h-[85vh] flex flex-col transition-[max-width] duration-300 ${
            activeTab === 'rates'
              ? 'w-[96vw] sm:max-w-[min(1200px,96vw)]'
              : 'sm:max-w-[560px]'
          }`}
        >
          <DialogHeader>
            <DialogTitle>
              {mode === 'create'
                ? 'Add Insurance'
                : isViewOnly
                  ? 'View Insurance'
                  : 'Edit Insurance'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? 'Create a new insurance provider for a clinic.'
                : isViewOnly
                  ? 'This insurance is synced from WellSky and managed by the clinic-info sync.'
                  : 'Update insurance provider details.'}
            </DialogDescription>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={v => {
              setActiveTab(v as InsuranceModalTab);
              if (v === 'rates') setRatesTabVisited(true);
            }}
            className='flex min-h-0 flex-1 flex-col'
          >
            {showRatesTab && (
              <TabsList className='w-fit'>
                <TabsTrigger value='details'>Details</TabsTrigger>
                <TabsTrigger
                  value='rates'
                  disabled={!canEditRates}
                  title={
                    !canEditRates ? 'Save the insurance first.' : undefined
                  }
                >
                  Billing rates{ratesCount != null ? ` (${ratesCount})` : ''}
                </TabsTrigger>
              </TabsList>
            )}

            <TabsContent
              value='details'
              className='flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden'
            >
              <div className='overflow-y-auto flex-1 pr-1 space-y-2'>
                {isViewOnly && (
                  <div className='flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground'>
                    <RefreshCw className='h-4 w-4 shrink-0 mt-0.5' />
                    <span>
                      Synced from WellSky. All fields are read-only here because
                      clinic-info sync keeps this record aligned with Kinnser.
                    </span>
                  </div>
                )}

                {/* Insurance details */}
                <SectionHeader>Insurance details</SectionHeader>

                {mode === 'create' && (
                  <ClinicSelect
                    label='Clinic'
                    clinics={clinics}
                    required
                    value={form.agency_clinic_id}
                    onChange={e =>
                      onChange({ agency_clinic_id: e.target.value })
                    }
                    placeholder='Select a clinic'
                  />
                )}

                <Field
                  label='Insurance name'
                  htmlFor='ins-name'
                  required
                  value={form.name}
                  onChange={value => onChange({ name: value })}
                  copyable={false}
                  inputProps={{ disabled: isViewOnly }}
                />

                {/* Stedi payer-database lookup — autofills Insurance name + Payer ID.
              Hidden for WellSky-synced rows since the modal is view-only. */}
                {!isViewOnly && (
                  <div className='flex flex-col gap-1.5'>
                    <label className='text-sm font-medium'>
                      Lookup from payer database
                    </label>
                    <div className='flex gap-2'>
                      <Input
                        placeholder='Search by name or ID...'
                        value={payorSearchQuery}
                        onChange={e => setPayorSearchQuery(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handlePayorSearch();
                          }
                        }}
                        className='flex-1'
                      />
                      <Button
                        type='button'
                        variant='outline'
                        onClick={handlePayorSearch}
                        disabled={isSearchingPayors || !payorSearchQuery.trim()}
                      >
                        {isSearchingPayors ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <Search className='h-4 w-4' />
                        )}
                      </Button>
                    </div>
                    {showPayorResults && (
                      <div className='max-h-[200px] overflow-y-auto rounded-md border'>
                        {payorSearchResults.length === 0 ? (
                          <div className='p-3 text-sm text-muted-foreground text-center'>
                            {isSearchingPayors
                              ? 'Searching...'
                              : 'No payors found'}
                          </div>
                        ) : (
                          payorSearchResults.map(payor => (
                            <button
                              key={payor.id}
                              type='button'
                              className='w-full text-left px-3 py-2 hover:bg-accent text-sm border-b last:border-b-0 cursor-pointer'
                              onClick={() => handleSelectPayor(payor)}
                            >
                              <div className='font-medium'>
                                {payor.payor_name}
                              </div>
                              {payor.electronic_payor_id && (
                                <div className='text-muted-foreground text-xs'>
                                  Payer ID: {payor.electronic_payor_id}
                                </div>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                <DropdownField
                  label='Payer type'
                  htmlFor='ins-payor-type'
                  required={mode === 'create'}
                  variant='formSelect'
                  options={PAYOR_TYPE_OPTIONS}
                  value={form.payor_type ?? ''}
                  onChange={value => {
                    const pt = value ? (value as PayorType) : null;
                    // PDGM only applies to Medicare; default it On for Medicare and
                    // force it Off (and hide the toggle) for any other payer type.
                    onChange({
                      payor_type: pt,
                      pdgm_enabled: isMedicarePayorType(pt),
                    });
                  }}
                  placeholder='—'
                  disabled={isViewOnly}
                />

                <Field
                  label='Address line 1'
                  htmlFor='ins-address-one'
                  value={form.address_one}
                  onChange={value => onChange({ address_one: value })}
                  copyable={false}
                  inputProps={{ disabled: isViewOnly }}
                />

                <Field
                  label='Address line 2'
                  htmlFor='ins-address-two'
                  value={form.address_two}
                  onChange={value => onChange({ address_two: value })}
                  copyable={false}
                  inputProps={{ disabled: isViewOnly }}
                />

                <div className='grid grid-cols-3 gap-2'>
                  <div className='col-span-1'>
                    <Field
                      label='City'
                      htmlFor='ins-city'
                      value={form.city}
                      onChange={value => onChange({ city: value })}
                      copyable={false}
                      inputProps={{ disabled: isViewOnly }}
                    />
                  </div>
                  <div className='col-span-1'>
                    <Field
                      label='State'
                      htmlFor='ins-state'
                      value={form.state}
                      onChange={value => onChange({ state: value })}
                      copyable={false}
                      inputProps={{ maxLength: 2, disabled: isViewOnly }}
                    />
                  </div>
                  <div className='col-span-1'>
                    <Field
                      label='ZIP'
                      htmlFor='ins-zip'
                      value={form.zip_code}
                      onChange={value => onChange({ zip_code: value })}
                      copyable={false}
                      inputProps={{ maxLength: 10, disabled: isViewOnly }}
                    />
                  </div>
                </div>

                <Field
                  label='Provider code'
                  htmlFor='ins-provider-code'
                  value={form.provider_code}
                  onChange={value => onChange({ provider_code: value })}
                  copyable={false}
                  inputProps={{ disabled: isViewOnly }}
                />

                <Field
                  label='Provider number'
                  htmlFor='ins-provider-number'
                  value={form.provider_number}
                  onChange={value => onChange({ provider_number: value })}
                  copyable={false}
                  inputProps={{ disabled: isViewOnly }}
                />

                {/* Billing — gated behind the edit-insurance-billing feature flag */}
                {showBillingConfig && (
                  <>
                    <SectionHeader>Billing</SectionHeader>

                    <DropdownField
                      label='Billing frequency'
                      htmlFor='ins-billing-frequency'
                      variant='formSelect'
                      options={BILLING_FREQUENCY_OPTIONS}
                      value={form.billing_frequency}
                      onChange={value =>
                        onChange({
                          billing_frequency: value as BillingFrequency,
                        })
                      }
                      disabled={isViewOnly}
                    />

                    <DropdownField
                      label='Charge type'
                      htmlFor='ins-charge-type'
                      variant='formSelect'
                      options={INSURANCE_CHARGE_TYPE_OPTIONS}
                      value={form.charge_type}
                      onChange={value =>
                        onChange({ charge_type: value as ChargeType })
                      }
                      disabled={isViewOnly}
                    />

                    <Field
                      label='Health plan ID (UB-04 locator 51)'
                      htmlFor='ins-health-plan-id'
                      value={form.health_plan_id}
                      onChange={value => onChange({ health_plan_id: value })}
                      copyable={false}
                      inputProps={{ disabled: isViewOnly }}
                    />

                    {/* PDGM only applies to Medicare payer types. */}
                    {isMedicarePayorType(form.payor_type) && (
                      <SwitchRow
                        id='ins-pdgm'
                        label='PDGM enabled'
                        checked={form.pdgm_enabled}
                        onCheckedChange={checked =>
                          onChange({ pdgm_enabled: checked })
                        }
                        disabled={isViewOnly}
                      />
                    )}

                    <SwitchRow
                      id='ins-no-multiply'
                      label='Do not multiply charges by units'
                      checked={form.do_not_multiply_charges_by_units}
                      onCheckedChange={checked =>
                        onChange({ do_not_multiply_charges_by_units: checked })
                      }
                      disabled={isViewOnly}
                    />
                  </>
                )}

                {/* EDI & authorization */}
                <SectionHeader>EDI &amp; authorization</SectionHeader>

                <Field
                  label='Payer ID'
                  htmlFor='ins-payer-id'
                  value={form.edi_payer_id}
                  onChange={value => onChange({ edi_payer_id: value })}
                  copyable={false}
                  inputProps={{ disabled: isViewOnly }}
                />

                <Field
                  label='Clearinghouse'
                  htmlFor='ins-clearinghouse'
                  value={form.edi_clearinghouse}
                  onChange={value => onChange({ edi_clearinghouse: value })}
                  copyable={false}
                  inputProps={{ disabled: isViewOnly }}
                />

                <SwitchRow
                  id='ins-auth-required'
                  label='Authorization required'
                  checked={form.visit_authorization_required}
                  onCheckedChange={checked =>
                    onChange({ visit_authorization_required: checked })
                  }
                  disabled={isViewOnly}
                />

                <SwitchRow
                  id='ins-noa-required'
                  label='NOA required'
                  checked={form.noa_required}
                  onCheckedChange={checked =>
                    onChange({ noa_required: checked })
                  }
                  disabled={isViewOnly}
                />
              </div>

              <DialogFooter className='pt-3'>
                <Button variant='outline' onClick={requestClose}>
                  {isViewOnly ? 'Close' : 'Cancel'}
                </Button>
                {!isViewOnly && (
                  <Button onClick={requestSave} disabled={isSaveDisabled}>
                    {saving && (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    )}
                    {mode === 'create' ? 'Create Insurance' : 'Save Changes'}
                  </Button>
                )}
              </DialogFooter>
            </TabsContent>

            {/* Rendered only once the tab has been visited (lazy first
              mount), then forceMount keeps in-progress rate edits alive
              across tab switches (Radix unmounts inactive tabs by default). */}
            {showRatesTab && canEditRates && insuranceId && ratesTabVisited && (
              <TabsContent
                value='rates'
                forceMount
                className='flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden'
              >
                <InsuranceRatesPanel
                  insuranceId={insuranceId}
                  wellSkySynced={wellSkySynced}
                  inheritedChargeType={form.charge_type}
                  onDirtyChange={setRatesDirty}
                  onRatesCountChange={setRatesCount}
                />
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={confirmDiscardOpen}
        title='Discard unsaved rate changes?'
        message='Your rate edits have not been saved. Close without saving?'
        confirmLabel='Discard'
        cancelLabel='Keep editing'
        onAccept={() => {
          setConfirmDiscardOpen(false);
          onCancel();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />

      <ConfirmationDialog
        open={confirmSaveDiscardOpen}
        title='Discard unsaved rate changes?'
        message='Saving the insurance closes this window, and your rate edits on the Billing rates tab have not been saved. Save the insurance and discard the rate edits?'
        confirmLabel='Save and discard rate edits'
        cancelLabel='Keep editing'
        onAccept={() => {
          setConfirmSaveDiscardOpen(false);
          onSave();
        }}
        onCancel={() => setConfirmSaveDiscardOpen(false)}
      />
    </>
  );
};

export default InsuranceModal;
